import { OdsVendorCard } from '@components/OdsVendorCard/OdsVendorCard';
import OrionVendorCardProps from '@components/card/helpers/vendor_card_props/OrionVendorCardProps';
import { MEDIA_QUERY_STATES } from '@constants/media-query';
import useAnalyticsContext from '@hooks/useAnalyticsContext';
import { useIsVisible } from '@hooks/useIsVisible';
import { useQueueVendorImpression } from '@hooks/useQueueVendorImpression';
import { useMedia } from '@tkww/orion-web-app-kernel';
import React, { useCallback, useContext, useMemo } from 'react';
import AnalyticsConstants from '../../../../../../constants/analytics';
import WeddingsContext from '../../../../../contexts/WeddingsContext/WeddingsContext';
import Vendor from '../../../../../decorators/vendor';
import VendorAnalytics from '../../../../../decorators/vendorAnalytics';
import { STOREFRONT_CENTER_IMPRESSION_TYPE } from '../types';
import { useCommonTrackingProperties } from '../utils';
import Styles from './TrackableVendorCard.scss';

interface TrackableVendorCardProps {
	vendor: Vendor.Raw;
	index: number;
	shouldTrack?: boolean;
	centerFavoriteSnackbar?: boolean;
}

export const TrackableVendorCard = ({
	vendor,
	index,
	shouldTrack = true,
	centerFavoriteSnackbar = false,
}: TrackableVendorCardProps) => {
	const { track } = useAnalyticsContext();
	const { onSaveHandler, getIsSaved } = useContext(WeddingsContext);
	const queueVendorImpression = useQueueVendorImpression();
	const vendorAnalytics = useMemo(
		() => new VendorAnalytics(new Vendor(vendor)),
		[vendor],
	);
	const commonTrackingProperties = useCommonTrackingProperties();
	const isMobileSize = useMedia('@mobile') === MEDIA_QUERY_STATES.matched;

	const impressionCallback = () => {
		if (!shouldTrack) {
			return;
		}

		queueVendorImpression({
			locationIndex: index + 1,
			impressionType: STOREFRONT_CENTER_IMPRESSION_TYPE,
			vendor,
		});
	};

	const [targetRef] = useIsVisible<HTMLDivElement>({
		callback: impressionCallback,
	});

	const handleClick = useCallback(() => {
		track({
			event: AnalyticsConstants.CLICK_THROUGH_TO_VENDORS,
			properties: {
				...vendorAnalytics.baseEventProps(),
				...commonTrackingProperties,
				impressionPosition: index + 1,
			},
		});
	}, [track, vendorAnalytics, commonTrackingProperties, index]);

	return (
		<div ref={targetRef} onClick={handleClick} onKeyDown={handleClick}>
			<OdsVendorCard
				key={vendor.id}
				{...OrionVendorCardProps(vendor, true, true, true)}
				size={isMobileSize ? 'sm' : 'md'}
				actionsOverlay={{
					actionType: 'save',
					onClick: (event) => {
						event.stopPropagation();
						onSaveHandler(vendor, undefined, true, commonTrackingProperties);
					},
					force: true,
				}}
				isFavorite={getIsSaved(vendor.id)}
				classes={{
					container: Styles.container,
					'actions-overlay': Styles.actionsOverlay,
					'main-action-container': Styles.cardContainer,
					'image-container': Styles.cardImage,
					'vendor-name': Styles.name,
					'rating-container': Styles.ratingContainer,
					location: Styles.location,
					'info-statements-container': Styles.infoStatementsContainer,
					'info-statement': Styles.infoStatement,
				}}
				centerFavoriteSnackbar={centerFavoriteSnackbar}
				vendorRaw={vendor}
			/>
		</div>
	);
};
